// Bio's live adapter (Track 2, T2-3): app-api's `/player`, `/player-current-league`,
// `/competitions` and one `POST /player-stats` → the same ProtoPlayerBio the snapshot hook
// returns, so the widget doesn't change. Works for ANY player id — the snapshot's or not.

import { rememberFamiliarName } from "../data/familiar-names.js";
import { mainPositionGroup } from "@catbento/shared";
import type {
  CompetitionsResponse,
  PlayerBio,
  PlayerBioResponse,
  PlayerCurrentLeague,
  PlayerStatsRequest,
  PlayerStatsResponse,
  PlayerStatsRow,
  Position,
  ProtoPlayerBio,
} from "@catbento/shared";
import type { CompetitionId } from "../data/league.js";
import type { PlayerId } from "../data/players.js";
import { getPlayerShortName } from "../data/player-utils.js";
import type { LiveApi } from "./api.js";
import { leagueChoices } from "./league.js";
import { rememberKeeper, rememberWomens } from "./player-gender.js";

/**
 * The position template from a player's minutes per position (one league + season): the
 * position she played most → its template (Catalyst's rule, §8.6 — decided for every player,
 * not the snapshot's tag). The templates ARE the aggregate layer's 6 groups, so the rule lives
 * in @catbento/shared (`mainPositionGroup`): the nightly job puts each player in her group by
 * the very same rule (T2-5a). Null when she has no minutes there.
 */
export function positionTemplateOf(rows: PlayerStatsRow[]): string | null {
  return mainPositionGroup(rows);
}

/** The request behind `positionTemplateOf` — minutes per position, one league + season. */
export function minutesByPositionRequest(playerId: PlayerId, competitionId: CompetitionId, seasonId: number): PlayerStatsRequest {
  return {
    keys: ["minutes"],
    where: { player_id: [playerId], competition_id: [competitionId], season_id: [seasonId] },
    groupBy: ["position_id"],
  };
}

/** Her minutes at each position (named: "Left Back"), most first; positions with no minutes left out. Pure. */
export function positionMinutesOf(
  rows: PlayerStatsRow[],
  positions: readonly Position[],
): { id: string; position: string; minutes: number }[] {
  const names = new Map(positions.map((p) => [p.id, p.name] as const));
  return rows
    .map((r) => {
      const id = r.position_id;
      const minutes = r.minutes;
      if (typeof id !== "string" || typeof minutes !== "number") throw new Error(`Unexpected minutes-by-position row: ${JSON.stringify(r)}`);
      const name = names.get(id);
      if (!name) throw new Error(`Position ${id} isn't in /positions`);
      return { id, position: name, minutes: Math.round(minutes) };
    })
    .filter((m) => m.minutes > 0)
    .sort((a, b) => b.minutes - a.minutes);
}

/**
 * The API's answers → the widget's shape.
 * - Team = her CURRENT club (`/player-current-league`, fork 1B), not `mostRecentTeam`:
 *   her last match can be for a national team. Last Appearance still shows that match.
 * - Competition = the label League Table shows for that league ("FAWSL" for a league
 *   the snapshot knows), or null when she has no domestic league on record.
 */
export function toProtoPlayerBio(
  player: PlayerBio,
  current: PlayerCurrentLeague,
  competition: string | null,
  positionTemplate: string | null,
): ProtoPlayerBio {
  // The Bio is where a dragged-in player is often met first: learn the name she's known by.
  rememberFamiliarName(player.name, player.nickname);
  return {
    name: player.name,
    shortName: getPlayerShortName(player),
    positionTemplate,
    team: current.teamName,
    competition,
    country: player.country,
    dob: player.dob,
    height: player.height,
    weight: player.weight,
    mostRecentMatchDate: player.mostRecentMatchDate,
    mostRecentTeam: player.mostRecentTeam,
    mostRecentOpponent: player.mostRecentOpponent,
    hopsRating: player.hops ? { rating: player.hops.rating } : null,
  };
}

/**
 * One player's Bio from the API. The three lookups go out together; the minutes-by-position
 * call needs her league + season first. Fail fast: an unknown id is `/player`'s 404 (the
 * widget's error state, never another player), and a current league missing from the
 * league list would be a bug.
 */
export async function loadPlayerBio(
  api: LiveApi,
  playerId: PlayerId,
  snapshotLeagueNames: ReadonlyMap<CompetitionId, string>,
): Promise<ProtoPlayerBio> {
  const [{ player }, current, { competitions }] = await Promise.all([
    api.get<PlayerBioResponse>(`/player?playerId=${playerId}`),
    api.get<PlayerCurrentLeague>(`/player-current-league?playerId=${playerId}`),
    api.get<CompetitionsResponse>("/competitions"),
  ]);
  if (current.competitionId === null) return toProtoPlayerBio(player, current, null, null);
  if (current.seasonId === null) throw new Error(`Player ${playerId}: a current league without a season`);

  const league = leagueChoices(competitions, snapshotLeagueNames).find((c) => c.id === current.competitionId);
  if (!league) throw new Error(`Player ${playerId}: current league ${current.competitionId} isn't in /competitions`);
  // Her gender, from her league, so a placed Bio can be dropped onto a live Stats / Radar tile
  // (the drop rule refuses a player whose gender isn't known yet: never a man with a woman).
  rememberWomens(playerId, league.womens);
  const { rows } = await api.post<PlayerStatsResponse>(
    "/player-stats",
    minutesByPositionRequest(playerId, current.competitionId, current.seasonId),
  );
  const position = positionTemplateOf(rows);
  rememberKeeper(playerId, position);
  return {
    ...toProtoPlayerBio(player, current, league.label, position),
    // Her league's gender picks the HOPS curve (Ben, 2026-10-18).
    live: { womens: league.womens },
  };
}
